import { differenceInYears, parse } from 'date-fns'
import * as R from 'ramda'
import { Team, TeamId } from 'src/schemas/Team'

import { CategoryId } from '../schemas/Category'
import { TournamentPlayer } from '../schemas/Player'
import { withoutUndefined } from './firestore'

export const ageOn = (date, birthDate) =>
  birthDate ? differenceInYears(parse(date), parse(birthDate)) : undefined

export const age = (birthDate) => (birthDate ? differenceInYears(new Date(), birthDate) : undefined)

const matchLevel = (level: string | undefined, playerNovice: boolean) => {
  if (level) {
    const lowerCaseLevel = level.toLowerCase()

    return (
      (lowerCaseLevel === 'novices' && playerNovice) ||
      (lowerCaseLevel === 'other' && !playerNovice)
    )
  }

  return true
}

const matchGender = (gender: string | undefined, playerGender: string) => {
  if (gender) {
    return gender.toLowerCase() === playerGender
  }

  return true
}

export const matchAge = (tournamentDateFrom, minAge, maxAge, playerBirthDate) => {
  const ageOnTournament = ageOn(tournamentDateFrom, playerBirthDate) as number // TODO refactor so that ageOn does not return undefined
  if (minAge && maxAge) {
    return minAge <= ageOnTournament && ageOnTournament <= maxAge
  }
  if (minAge) {
    return minAge <= ageOnTournament
  }
  if (maxAge) {
    return ageOnTournament <= maxAge
  }

  return true
}

const belongsTo = (tournamentDateFrom, player) => (category) =>
  matchLevel(category.level, player.novice) &&
  matchGender(category.gender, player.gender) &&
  matchAge(tournamentDateFrom, category.minAge, category.maxAge, player.birthDate)

const categoriesLens = R.lensProp('categories')

export const asTournamentPlayer = (tournamentDateFrom, categories) => (globalPlayer) => {
  const tournamentPlayer = R.pick(
    [
      'id',
      'firstName',
      'lastName',
      'gender',
      'nationality',
      'novice',
      'club',
      'czechRegistrationId',
    ],
    globalPlayer,
  )
  const assignedCategories = categories
    .filter(belongsTo(tournamentDateFrom, globalPlayer))
    .map((category) => category.id)

  return R.set(categoriesLens, assignedCategories, tournamentPlayer)
}

export const reprocessTournamentPlayers = (tournamentDateFrom, categories, globalPlayers) => (
  tournamentPlayers,
) =>
  tournamentPlayers.map((tournamentPlayer) => {
    const globalPlayer = R.find<FIXME>((player) => player.id === tournamentPlayer.id, globalPlayers)

    const reprocessedTournamentPlayer = globalPlayer
      ? asTournamentPlayer(tournamentDateFrom, categories)(globalPlayer)
      : {} // in case the find fails, do not perform any reprocessing

    return {
      ...tournamentPlayer, // keep fields present only on tournamentPlayer
      ...reprocessedTournamentPlayer,
    }
  })

const filterByCategory = (categoryId: CategoryId, tournamentPlayers: TournamentPlayer[]) =>
  R.filter(
    (tournamentPlayer) =>
      R.any(
        (playerCategoryId) => playerCategoryId === categoryId,
        R.view(categoriesLens, tournamentPlayer),
      ),
    tournamentPlayers,
  )

const removeCategoryFromPlayers = (categoryId: CategoryId, tournamentPlayers: TournamentPlayer[]) =>
  R.map(
    (tournamentPlayer) =>
      R.over(
        categoriesLens,
        R.filter((playerCategoryId) => playerCategoryId !== categoryId),
        tournamentPlayer,
      ),
    tournamentPlayers,
  )

export const withRemovedCategory = (categoryId, tournamentPlayers) =>
  removeCategoryFromPlayers(categoryId, filterByCategory(categoryId, tournamentPlayers))

const appendTeamPosition = (member) => (teamMembers) =>
  teamMembers ? R.sort(R.subtract, [member, ...teamMembers]) : [member]

export const getTeamsAndPositions = (
  globalTeams: Record<TeamId, Team>,
  tournamentPlayers: Array<Pick<TournamentPlayer, 'id' | 'team'>>,
): { teams: Record<TeamId, Team>; teamPositions: Record<TeamId, number[]> } => {
  return tournamentPlayers.reduce(
    ({ teams, teamPositions }, player) =>
      player.team
        ? {
            teams: teams[player.team.id]
              ? teams
              : {
                  ...teams,
                  [player.team.id]: {
                    type: 'tournament',
                    id: player.team.id,
                    num: Number(player.team.id),
                  },
                },
            teamPositions: R.over(
              R.lensProp(String(player.team.id)),
              appendTeamPosition(player.team.position),
              teamPositions,
            ),
          }
        : { teams, teamPositions },
    { teams: globalTeams, teamPositions: {} } as {
      teams: Record<TeamId, Team>
      teamPositions: Record<TeamId, number[]>
    },
  )
}

const teamPositionLens = R.lensPath(['team', 'position'])

export const withRemovedTeamMember = (tournamentPlayers) => (removedTournamentPlayer) =>
  tournamentPlayers
    .filter(
      (tournamentPlayer) =>
        tournamentPlayer.team &&
        tournamentPlayer.team.id === removedTournamentPlayer.team.id &&
        tournamentPlayer.team.position > removedTournamentPlayer.team.position,
    )
    .map(R.over(teamPositionLens, R.add(-1)))

export const swapTeamPosition = (tournamentPlayerLeft, tournamentPlayerRight) => [
  R.set(teamPositionLens, R.view(teamPositionLens, tournamentPlayerRight), tournamentPlayerLeft),
  R.set(teamPositionLens, R.view(teamPositionLens, tournamentPlayerLeft), tournamentPlayerRight),
]

export const addToTeam = (teamPositions: Record<TeamId, number[]>) => (
  tournamentPlayer,
  selectedTeamId,
  selectedTeamName?: string,
) => {
  const lastInTeam = R.last(teamPositions[selectedTeamId] || [0]) || 0

  return R.assoc(
    'team',
    withoutUndefined({ id: selectedTeamId, name: selectedTeamName, position: lastInTeam + 1 }),
    tournamentPlayer,
  )
}



// WEBPACK FOOTER //
// ./src/helpers/players.ts